import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import AppVersion from "./components/AppVersion";
import AuthPending from "./components/AuthPending";
import Navbar from "./components/Navbar";
import { AuthProvider } from "./contexts/AuthContext";
import TeamScopeProvider from "./contexts/TeamScopeProvider";
import { useAuth } from "./contexts/useAuth";
import CaptainRoute from "./components/CaptainRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";

const Welcome = lazy(() => import("./components/welcome"));
const EventsPage = lazy(() => import("./features/calendar/EventsPage"));
const TeamPage = lazy(() => import("./features/team/TeamPage"));
const TrainingPage = lazy(() => import("./features/training/TrainingPage"));
const MatchDayPage = lazy(() => import("./features/matches/MatchDayPage"));
const AdminPage = lazy(() => import("./features/admin/AdminPage"));

/** Signed in there is nothing to welcome anybody to - the team page is the app. */
function Home() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <AuthPending />;

  return isAuthenticated ? <Navigate to="/team" replace /> : <Welcome />;
}

function Protected({ children }: { children: ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function App() {
  return (
    <AuthProvider>
      <TeamScopeProvider>
        <Router>
          <div className="app-shell">
            <Navbar />
            <main className="app-content">
              <Suspense fallback={<div className="page-feedback">Ansicht wird geladen...</div>}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/termine" element={<Protected><EventsPage /></Protected>} />
                  <Route path="/team" element={<Protected><TeamPage /></Protected>} />
                  <Route path="/training" element={<Protected><TrainingPage /></Protected>} />
                  <Route path="/spieltag" element={<Protected><MatchDayPage /></Protected>} />
                  <Route path="/verwaltung" element={<Protected><CaptainRoute><AdminPage /></CaptainRoute></Protected>} />

                  {/*
                    The eight former destinations. Bookmarks and links in the handout
                    keep working; each one lands where its content lives now.
                  */}
                  <Route path="/statistiken" element={<Navigate to="/team" replace />} />
                  <Route path="/aufstellung" element={<Navigate to="/team" replace />} />
                  <Route path="/turnier" element={<Navigate to="/training" replace />} />
                  <Route path="/training/*" element={<Navigate to="/training" replace />} />
                  <Route path="/spiel-anlegen" element={<Navigate to="/spieltag" replace />} />
                  <Route path="/spiele" element={<Navigate to="/spieltag" replace />} />
                  <Route path="/spieler-anlegen" element={<Navigate to="/verwaltung" replace />} />
                  <Route path="/saison" element={<Navigate to="/verwaltung" replace />} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </main>
            <AppVersion />
          </div>
        </Router>
      </TeamScopeProvider>
    </AuthProvider>
  );
}

export default App;
